/**
 * Chart geometry. No domain content: which band is current and when a chain starts are the
 * server's answers, and this file only decides where they are drawn.
 *
 * The x-scale works in days-since-epoch because that is a pure mapping of a calendar day onto a
 * number line for drawing, not date arithmetic: nothing here produces a date, and no result of
 * it is ever shown to a user as one.
 */

/** The character-width approximation the label pack uses. Cheaper than measuring, and stable. */
const CHARACTER_WIDTH = 5.4;
const LABEL_PADDING = 10;
/** A band narrower than this cannot hold its name, so the name is dropped rather than clipped. */
export const MINIMUM_BAND_WIDTH = 92;

export interface MeasuredLabel {
  text: string;
  x: number;
  /** Right-anchored, because the anchor sits in the right-hand 28% of the chart. */
  flip: boolean;
  from: number;
  to: number;
}

export function measureLabel(text: string, x: number, flip: boolean): MeasuredLabel {
  const width = text.length * CHARACTER_WIDTH + LABEL_PADDING;
  return { text, x, flip, from: flip ? x - width : x, to: flip ? x : x + width };
}

/**
 * A greedy first-fit shelf pack: each label goes in the first row whose last occupant ended more
 * than `minGap` before this one starts. It is the reason two events a year apart do not print on
 * top of each other, and the returned row count is what the chart sizes itself from.
 *
 * Sorted before placing. The prototype has two copies of this and they had already drifted on
 * exactly that point.
 */
export function layoutLabelRows<T extends MeasuredLabel>(
  labels: readonly T[],
  minGap = 6,
): { rows: number; placed: Array<T & { row: number }> } {
  const ordered = [...labels].sort((a, b) => a.from - b.from);
  const shelfEnds: number[] = [];
  const placed: Array<T & { row: number }> = [];

  for (const label of ordered) {
    let row = shelfEnds.findIndex((end) => label.from > end + minGap);
    if (row === -1) {
      row = shelfEnds.length;
      shelfEnds.push(label.to);
    } else {
      shelfEnds[row] = label.to;
    }
    placed.push({ ...label, row });
  }

  return { rows: Math.max(1, shelfEnds.length), placed };
}

/** Days since the Unix epoch, for placing a calendar day on a proportional axis. */
export function axisPoint(iso: string): number {
  const [year, month, day] = iso.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return 0;
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

export interface Scale {
  (iso: string): number;
  domain: readonly [number, number];
}

export function linearScale(
  from: string,
  to: string,
  range: readonly [number, number],
): Scale {
  const low = axisPoint(from);
  const high = axisPoint(to);
  const span = high - low || 1;
  const [left, right] = range;

  const scale = ((iso: string) => left + ((axisPoint(iso) - low) / span) * (right - left)) as Scale;
  Object.defineProperty(scale, 'domain', { value: [low, high] as const });
  return scale;
}

/** Keeps a label that would print off either end pinned inside the padding. */
export function clampToChart(x: number, left: number, right: number): number {
  return Math.min(Math.max(x, left), right);
}
